import React, { lazy } from 'react';
import Logout from './components/Logout';
import { wrapIntoSinglePanel } from './components/SinglePanelWrapper';

export const HOME_PAGE_ALIASES = ['/', '/login', '/cdcservlet'];
export const MANDATES_PAGE_PATH = '/mandates';
export const DEALER_MANDATES_PATH = '/admin/mandates';
export const DEALER_SET_REPRESENTATION_PATH = '/admin/set-representation';
export const DEALER_CONFIG_PATH = '/admin/config';
export const DEALER_SESSION_EXPIRY_NOTICE = '/admin/session-expiration-notification';
export const LOGOUT_PAGE = '/logout';
export const LARES_POST_FORM_PATH = '/lares-redirect';
export const EMAIL_SINGLE_PANEL_PATH = '/email/login';
export const EMAIL_REGISTER_PATH = '/email/register';
export const EMAIL_FORGOT_PASSWORD_PATH = '/email/forgot-password';
export const EMAIL_NEW_PASSWORD_PATH = '/email/new-password';
export const ACCOUNT_MERGE_PATH = '/email/merge';
export const LOGIN_RETURN = '/login/return';
export const BANK = '/bank/auth/:authService';
export const ADD_EMAIL = '/email/add';
export const SESSION_TRANSFER = '/transfer-session';
export const ACCOUNT = '/account';
export const EMAIL_FORCE_PASSWORD_RESET_PATH = '/email/force-password-reset';

export const UI_WEB_API_PATH = '/ui/webapi';
export const UI_LOGOUT_PAGE_PATH = '/ui/webapi/logout';

export const SSO_HEADER_INCLUDED_PAGES = [
  ACCOUNT,
  '/',
  '/login',
  '/cdcservlet',
  ACCOUNT_MERGE_PATH,
  EMAIL_FORGOT_PASSWORD_PATH,
  EMAIL_REGISTER_PATH,
  EMAIL_NEW_PASSWORD_PATH,
  EMAIL_SINGLE_PANEL_PATH,
  SESSION_TRANSFER,
  EMAIL_FORCE_PASSWORD_RESET_PATH,
];

export enum View {
  LARES = 'LARES',
  MANDATES = 'MANDATES',
  BANK_FORM = 'BANK_FORM',
  WEB_API = 'WEB_API',
  ACCOUNT_MERGE = 'ACCOUNT_MERGE',
  ACCOUNT = 'ACCOUNT',
  EMAIL_RESET_REQUESTED = 'EMAIL_RESET_REQUESTED',
  EMAIL_FORCE_PASSWORD_RESET = 'EMAIL_FORCE_PASSWORD_RESET',
}

export interface RouteProps {
  path: string;
  component: React.ReactElement | null;
  pageTitleLabel?: string;
  exact?: boolean;
  name?: View;
}

const HomePage = lazy(() => import('./pages/HomePage'));
const MandatesPage = lazy(() => import('./pages/MandatesPage'));
const LogoutPage = lazy(() => import('./pages/LogoutPage'));
const LaresPostFormPage = lazy(() => import('./pages/LaresPostFormPage'));
const DealerPreferences = lazy(() => import('./pages/DealerPreferences'));
const DealerMandatesPage = lazy(() => import('./pages/DealerMandatesPage'));
const DealerSetRepresentationPage = lazy(() => import('./pages/DealerSetRepresentationPage'));
const SessionTransfer = lazy(() => import('./pages/SessionTransferPage'));
const BankFormWrapper = lazy(() => import('./login/banks/BankFormWrapper'));
const LoginReturn = lazy(() => import('./login/LoginReturn'));
const EmailLogin = lazy(() => import('./login/EmailLogin'));
const AccountMerge = lazy(() => import('./email/AccountMerge'));
const EmailRegister = lazy(() => import('./register/EmailRegister'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const AddEmail = lazy(() => import('./email/AddEmail'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const NotFound = lazy(() => import('./notfound/NotFound'));
const DealerSessionExpirationNotice = lazy(() => import('./pages/DealerSessionExpirationNotice'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const ForcePasswordReset = lazy(() => import('./components/ForcePasswordReset'));
const WebApi = lazy(() => import('./components/WebApi'));

export const ROUTES: RouteProps[] = [
  {
    path: UI_LOGOUT_PAGE_PATH,
    component: <Logout />,
  },
  {
    path: UI_WEB_API_PATH,
    name: View.WEB_API,
    component: <WebApi />,
  },
  {
    path: '/',
    component: <HomePage />,
  },
  {
    path: '/login',
    component: <HomePage />,
  },
  {
    path: '/cdcservlet',
    component: <HomePage />,
  },
  {
    path: MANDATES_PAGE_PATH,
    component: <MandatesPage />,
    name: View.MANDATES,
    pageTitleLabel: 'pageTitle.MANDATES',
  },
  {
    path: LOGOUT_PAGE,
    component: <LogoutPage />,
    pageTitleLabel: 'pageTitle.LOGOUT',
  },
  {
    path: LARES_POST_FORM_PATH,
    component: <LaresPostFormPage />,
    name: View.LARES,
  },
  {
    path: DEALER_CONFIG_PATH,
    component: <DealerPreferences />,
  },
  {
    path: DEALER_MANDATES_PATH,
    component: <DealerMandatesPage />,
  },
  {
    path: DEALER_SET_REPRESENTATION_PATH,
    component: <DealerSetRepresentationPage />,
  },
  {
    path: DEALER_SESSION_EXPIRY_NOTICE,
    component: <DealerSessionExpirationNotice />,
  },
  {
    path: BANK,
    component: <BankFormWrapper />,
    name: View.BANK_FORM,
  },
  {
    path: LOGIN_RETURN,
    component: <LoginReturn />,
  },
  {
    path: EMAIL_SINGLE_PANEL_PATH,
    component: wrapIntoSinglePanel(EmailLogin),
  },
  {
    path: ACCOUNT_MERGE_PATH,
    component: <AccountMerge />,
    name: View.ACCOUNT_MERGE,
    pageTitleLabel: 'pageTitle.ACCOUNT_MERGE',
  },
  {
    path: EMAIL_REGISTER_PATH,
    component: <EmailRegister />,
    pageTitleLabel: 'pageTitle.EMAIL_REGISTER',
  },
  {
    path: EMAIL_FORGOT_PASSWORD_PATH,
    component: wrapIntoSinglePanel(ForgotPassword),
    pageTitleLabel: 'pageTitle.EMAIL_FORGOT_PASSWORD',
  },
  {
    path: ADD_EMAIL,
    component: <AddEmail />,
    pageTitleLabel: 'pageTitle.ADD_EMAIL',
  },
  {
    path: EMAIL_NEW_PASSWORD_PATH,
    component: wrapIntoSinglePanel(ResetPassword),
  },
  {
    path: SESSION_TRANSFER,
    component: wrapIntoSinglePanel(SessionTransfer),
    pageTitleLabel: 'pageTitle.SESSION_TRANSFER',
  },
  {
    path: ACCOUNT,
    component: <AccountPage />,
    name: View.ACCOUNT,
    pageTitleLabel: 'pageTitle.ACCOUNT',
  },
  {
    path: EMAIL_FORCE_PASSWORD_RESET_PATH,
    component: wrapIntoSinglePanel(ForcePasswordReset),
    name: View.EMAIL_FORCE_PASSWORD_RESET,
    pageTitleLabel: 'pageTitle.EMAIL_FORGOT_PASSWORD',
  },
  {
    path: '*',
    component: <NotFound />,
  },
];
